import type { ReactNode } from "react";
import { AnimatePresence } from "motion/react";
import { Type } from "@themes/pollinator/ui/Type";
import { StepActions } from "@themes/pollinator/ui/StepActions";
import { Loader } from "@themes/pollinator/ui/Loader";
import { MotionFade } from "@themes/pollinator/ui/MotionFade";
import { TwoPanelLayout } from "@themes/pollinator/ui/TwoPanelLayout";

export type Props = {
  /** Main headline for the left panel */
  title: string;
  /** Description text below the headline */
  description: string;
  /** Current step index (1-based for display) */
  stepIndex: number;
  /** Total number of steps */
  totalSteps: number;
  /** Label for the primary action button */
  buttonLabel: string;
  /** Whether the button is disabled */
  isButtonDisabled?: boolean;
  /** Callback when the primary button is clicked */
  onButtonClick: () => void;
  /** Whether to show the back button */
  showBackButton?: boolean;
  /** Callback when the back button is clicked */
  onBackClick?: () => void;
  /** Whether content is loading */
  isLoading?: boolean;
  /** Quiz content to render in the right panel */
  children: ReactNode;
  /** Additional className for the root element */
  className?: string;
};

/**
 * Two-panel layout for the fit quiz onboarding flow.
 *
 * Left panel (dark, ~60%): Headline, description
 * Right panel (light, ~40%): Quiz content, footer with button
 */
export function FitQuizLayout({
  title,
  description,
  stepIndex,
  totalSteps,
  buttonLabel,
  isButtonDisabled = false,
  onButtonClick,
  showBackButton = false,
  onBackClick,
  isLoading = false,
  children,
  className,
}: Props) {
  const leftPanelContent = (
    <div className="flex flex-col flex-1 justify-center px-8 border-b border-brand-800 md:px-12 py-6 md:py-8 max-w-[640px]">
      <div className="flex flex-col gap-6">
        <Type variant="h1" as="h1" className="text-brand-800 md:text-brand-50">
          {title}
        </Type>
        <Type variant="body1" as="p" className="text-brand-800 md:text-brand-100">
          {description}
        </Type>
      </div>
    </div>
  );

  const rightPanelContent = (
    <>
      {/* Main Content Area - Quiz Content */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 overflow-y-auto">
        <div className="w-full max-w-[480px]">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <MotionFade key="loader">
                <Loader />
              </MotionFade>
            ) : (
              <MotionFade key="content">
                {children}
              </MotionFade>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer with StepActions */}
      <AnimatePresence>
        {!isLoading && (
          <MotionFade key="footer">
            <StepActions
              stepIndex={stepIndex}
              totalSteps={totalSteps}
              primaryLabel={buttonLabel}
              onPrimaryClick={onButtonClick}
              isPrimaryDisabled={isButtonDisabled}
              onBack={showBackButton ? onBackClick : undefined}
            />
          </MotionFade>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <TwoPanelLayout
      testId="fit-quiz-layout"
      className={className}
      leftPanel={leftPanelContent}
      rightPanel={rightPanelContent}
    />
  );
}

export default FitQuizLayout;
